import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { Search, X, FileText, Clock, Tag, ChevronRight, RefreshCw, Paperclip, Loader } from 'lucide-react';
import Modal from '../shared/Modal';
import { sanitizeTemplateRecord, searchText } from '../../lib/piiEnvelope';

interface Attachment { name: string; base64Content: string; mimeType: string; }

interface Template {
  id: string;
  templateId: string;
  code?: string;
  name: string;
  tags: string[];
  subject: string;
  htmlBody: string;
  attachments?: Attachment[];
  documentTemplateIds?: string[];
  version: number;
  updatedAt: string;
  hasAttachments?: boolean;
}

interface Props {
  onSelect: (template: { subject: string; htmlBody: string; name: string; attachments?: Attachment[]; documentTemplateIds?: string[] }) => void;
  onClose: () => void;
}

async function getIdToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

export default function TemplatePickerModal({ onSelect, onClose }: Props) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [selectingId, setSelectingId] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      const res = await fetch('/api/email-templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      // Defence in depth on top of the server-side boundary guard:
      // never let a non-string field (numeric code, envelope object)
      // into state — the search filter and the list render assume
      // strings and crashed on the stored shapes (React #31 /
      // "(code || '').toLowerCase is not a function").
      if (res.ok) setTemplates((data.templates || []).map((t: any) => sanitizeTemplateRecord(t)));
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const allTags = React.useMemo(() => {
    const tagSet = new Set<string>();
    templates.forEach(t => (t.tags || []).forEach(tag => {
      const s = searchText(tag);
      if (s) tagSet.add(s);
    }));
    return Array.from(tagSet).sort();
  }, [templates]);

  const handleSelectTemplate = async (t: Template) => {
    setSelectingId(t.id);
    try {
      const token = await getIdToken();
      const res = await fetch(`/api/email-templates/${t.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok && data.template) {
        // Sanitize the fetched record in place at this boundary so
        // the payload below is built from clean string fields.
        data.template = sanitizeTemplateRecord(data.template);
        onSelect({ subject: data.template.subject, htmlBody: data.template.htmlBody, name: data.template.name, attachments: data.template.attachments || [], documentTemplateIds: data.template.documentTemplateIds || [] });
      } else {
        onSelect({ subject: t.subject, htmlBody: t.htmlBody, name: t.name, attachments: t.attachments || [], documentTemplateIds: t.documentTemplateIds || [] });
      }
    } catch {
      onSelect({ subject: t.subject, htmlBody: t.htmlBody, name: t.name, attachments: t.attachments || [], documentTemplateIds: t.documentTemplateIds || [] });
    }
  };

  const filtered = React.useMemo(() => {
    let result = templates;
    if (search.trim()) {
      const q = search.toLowerCase();
      // Type-safe search: searchText never returns a non-string, so a
      // record whose fields arrived in an unexpected shape simply
      // does not match on that field instead of throwing. The item
      // itself stays visible whenever another field matches, and in
      // the unfiltered list.
      result = result.filter(t => searchText(t.name).toLowerCase().includes(q) || searchText(t.subject).toLowerCase().includes(q) || searchText(t.code).toLowerCase().includes(q) || (t.tags || []).some(tag => searchText(tag).toLowerCase().includes(q)));
    }
    if (filterTag) result = result.filter(t => (t.tags || []).includes(filterTag));
    return result;
  }, [templates, search, filterTag]);

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[80vh] flex flex-col animate-fade-in" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-brand" />
            <h3 className="text-sm font-extrabold text-slate-900">Load Email Template</h3>
          </div>
          <button aria-label="Close" title="Close" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>

        {/* Search + Tag filter */}
        <div className="p-4 border-b border-slate-100 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, subject, or tag..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  bg-slate-50" autoFocus />
          </div>
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setFilterTag(null)} className={`px-2.5 py-1 text-caption font-semibold rounded-lg transition-all cursor-pointer ${!filterTag ? 'btn-primary text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>All</button>
              {allTags.map(tag => (
                <button key={tag} onClick={() => setFilterTag(filterTag === tag ? null : tag)} className={`px-2.5 py-1 text-caption font-semibold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${filterTag === tag ? 'btn-primary text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                  <Tag className="h-3 w-3" /><span>{tag}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Template list */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="py-12 text-center"><RefreshCw className="animate-spin h-6 w-6 text-brand mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-bold text-slate-500">{search || filterTag ? 'No templates match your search' : 'No email templates yet'}</p>
              <p className="text-xs text-slate-500 mt-1">Create one from the Email Templates section.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map(t => (
                <button key={t.id} onClick={() => handleSelectTemplate(t)} disabled={selectingId === t.id} className="w-full text-left p-3 rounded-xl bg-brand-active transition-all cursor-pointer flex items-center justify-between group border border-transparent hover:border-blue-200 disabled:opacity-60 disabled:cursor-wait">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-bold text-slate-800 truncate">{t.code ? `[${t.code}] ` : ''}{t.name}</span>
                      {t.hasAttachments && <Paperclip className="h-3 w-3 text-blue-400 shrink-0" />}
                      {(t.tags || []).length > 0 && (
                        <div className="flex gap-1 shrink-0">
                          {t.tags.slice(0, 3).map(tag => <span key={tag} className="px-1.5 py-0.5 text-caption font-semibold bg-indigo-50 text-indigo-600 rounded-md">{tag}</span>)}
                          {t.tags.length > 3 && <span className="text-caption text-slate-400">+{t.tags.length - 3}</span>}
                        </div>
                      )}
                    </div>
                    <p className="text-caption text-slate-500 mt-0.5 truncate">{t.subject}</p>
                    <div className="flex items-center space-x-2 mt-1 text-caption text-slate-400">
                      <Clock className="h-3 w-3" /><span>{new Date(t.updatedAt).toLocaleDateString()}</span>
                      <span>v{t.version}</span>
                    </div>
                  </div>
                  {selectingId === t.id ? <Loader className="h-4 w-4 text-blue-500 animate-spin shrink-0 ml-2" /> : <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-blue-500 shrink-0 ml-2" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
